import React, { useState, useCallback, useEffect, useRef } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { showAlert } from "redux/actions/setAppActions";
import { getCondition } from "redux/actions/setConditionActions";
import { FINISH_LOADING, START_LOADING } from "redux/types";
import { CONSTRUCTOR_LINK, API_URL } from "config";
import { Loader } from "components/Loader";
import { Layout } from "components/Layout";
import { RangeAnswers } from "components/test/RangeAnswers";
import { SelectAnswers } from "components/test/SelectAnswers";
import { FreeFormAnswers } from "components/test/FreeFormAnswers";
/**
 * @api {get} /tests/:testId/:conditionId?type=type вопросы теста
 * @apiGroup tests
 * @apiName вопросы теста
 *
 * @apiParam {String} testId ID теста
 * @apiParam {String} conditionId ID методики
 *
 * @apiQuery {String} type (опционально) если в качесте типа указан "view", вопросы будут
 * показаны в демонстрационном режиме
 *
 * @apiDescription <p>Страница с вопросами по тесту</p>
 * <p>Компонент <code>TestPage.js</code> <a href="../client/src/pages/TestPage.js">ссылка на файл</a></p>
 */

export const TestPage = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation("tests");
    const history = useHistory();
    const location = useLocation();
    const { user, role } = useSelector((state) => state.auth);
    const { condition } = useSelector((state) => state.test);
    const { loading } = useSelector((state) => state.app);

    const [questionId, setQuestionId] = useState(0);
    const [questions, setQuestions] = useState([]);
    const [question, setQuestion] = useState(null);
    const [results, setResults] = useState([]);
    const [answers, setAnswers] = useState([]);
    const [end, setEnd] = useState(false);
    const [isConfirmPossible, setIsConfirmPossible] = useState(false);
    const [isViewTest, setIsViewTest] = useState(false);
    const [startTime, setStartTime] = useState(null);
    const [progressLoaded, setProgressLoaded] = useState(false);

    const conditionId = useParams().conditionId;
    const testId = useParams().testId;

    useEffect(() => {
        if (location.search) {
            const type = new URLSearchParams(location.search).get("type");
            if (type === "view") {
                setIsViewTest(true);
            }
        }
    }, [location]);

    useEffect(() => {
        dispatch(getCondition(conditionId));
    }, [conditionId, dispatch]);

    useEffect(() => {
        const loadProgress = async () => {
            if (isViewTest || role === "psych") {
                setProgressLoaded(true);
                return;
            }

            try {
                const response = await fetch(`${API_URL}tests/${testId}/progress`, {
                    credentials: "include",
                });
                const data = await response.json();

                if (data.isCompleted) {
                    history.push("/dashboard");
                    return;
                }

                if (data.currentQuestionId > 0) {
                    setQuestionId(data.currentQuestionId);
                    setResults(data.answers || []);
                    if (data.startTime) {
                        setStartTime(new Date(data.startTime).getTime());
                    }
                }
            } catch (e) {
                console.error("Failed to load progress:", e);
            } finally {
                setProgressLoaded(true);
            }
        };

        loadProgress();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [testId, isViewTest, role]);

    const postSolution = useCallback(async () => {
        if (role === "psych") return;
        dispatch({ type: START_LOADING });

        const json = condition.body;

        for (const [i, question] of json.questions.entries()) {
            if (question.type === "text") {
                question.value = results[i][0];
            } else {
                for (const answer of question.answers) {
                    if (results[i].includes(answer.id)) {
                        answer.selected = true;
                    }
                }
            }
        }

        /* eslint-disable no-throw-literal */
        fetch(`${API_URL}solution`, {
            method: "POST",
            body: JSON.stringify(json),
            headers: { "Content-Type": "application/json" },
            credentials: "include",
        })
            .then(async (res) => {
                if (!res.ok) {
                    const data = await res.json();
                    throw { message: data.message };
                } else {
                    return res.json();
                }
            })
            .then(async (res) => {
                const result = await fetch(`${API_URL}tests/solution`, {
                    method: "POST",
                    body: JSON.stringify({
                        solution: res,
                        rawSolution: json,
                        testId,
                        normStatus: true,
                        completionTime: Math.round((Date.now() - startTime) / 1000),
                    }),
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                });

                if (!result.ok) {
                    throw { message: t("error_message") };
                }

                return result.json();
            })
            .then(async () => {
                try {
                    await fetch(`${API_URL}tests/${testId}/progress`, {
                        method: "DELETE",
                        credentials: "include",
                    });
                } catch (e) {
                    console.error("Failed to delete progress:", e);
                }

                window.location.href = `/dashboard`;
                dispatch({ type: FINISH_LOADING });
            })
            .catch((e) => {
                const text = e.message || t("something_went_wrong", { ns: "translation" });
                dispatch({ type: FINISH_LOADING });
                dispatch(showAlert({ type: "error", text }));

                setTimeout(() => {
                    window.location.reload();
                }, 3000);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [condition, user, results, testId, conditionId, dispatch]);

    useEffect(() => {
        !!end && postSolution();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [end]);

    useEffect(() => {
        if (condition && condition.body) {
            setQuestions(condition.body.questions);
        }
    }, [condition]);

    useEffect(() => {
        if (questions.length) {
            const qw = questions[questionId];

            if (qw.questionType === "MultiSelection") {
                setQuestion({
                    name: qw.name,
                    image: qw.image,
                    type: qw.answerQuantity === 1 ? "select" : "multi",
                    quantity: qw.answerQuantity,
                    answers: qw.answers,
                });
            } else {
                setQuestion({
                    name: qw.name,
                    image: qw.image,
                    type: qw.type,
                    answers: qw.answers,
                });
            }

            if (results[questionId]) {
                setAnswers([...results[questionId]]);
            } else {
                if (["Range", "Text", "ReversedRange"].includes(qw.questionType)) {
                    setAnswers([""]);
                } else {
                    setAnswers(Array(qw.answers.length).fill(""));
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conditionId, questionId, questions]);

    const saveProgress = async (currentQuestion, currentResults, currentStartTime) => {
        if (isViewTest || role === "psych") return;

        try {
            await fetch(`${API_URL}tests/${testId}/progress`, {
                method: "POST",
                body: JSON.stringify({
                    currentQuestionId: currentQuestion,
                    answers: currentResults,
                    startTime: currentStartTime || Date.now(),
                }),
                headers: { "Content-Type": "application/json" },
                credentials: "include",
            });
        } catch (e) {
            console.error("Failed to save progress:", e);
        }
    };

    const advance = (newAnswers, updatedResults) => {
        const newStartTime = startTime || Date.now();
        if (!startTime) setStartTime(newStartTime);

        const nextQuestionId = questionId + 1;

        if (updatedResults.length >= questions.length) {
            setEnd(true);
        } else {
            saveProgress(nextQuestionId, updatedResults, newStartTime);
            setQuestionId(nextQuestionId);
        }
    };

    const confirmBtnHandler = () => {
        if (isConfirmPossible) {
            const updatedResults = [...results];
            updatedResults[questionId] = answers;
            setResults(updatedResults);
            advance(answers, updatedResults);
        }

        if (isViewTest) {
            const nextQuestionId = questionId + 1;
            if (nextQuestionId >= questions.length) {
                history.goBack();
            } else {
                setQuestionId(nextQuestionId);
            }
        }
    };

    const handleAnswerClick = (id, index) => {
        if (isViewTest) {
            const nextQuestionId = questionId + 1;
            if (nextQuestionId >= questions.length) history.goBack();
            else setQuestionId(nextQuestionId);
            return;
        }

        const newAnswers = answers.map((_, i) => (i === index ? (answers[i] === id ? "" : id) : ""));
        setAnswers(newAnswers);

        if (!newAnswers.join("")) return;

        const updatedResults = [...results];
        updatedResults[questionId] = newAnswers;
        setResults(updatedResults);
        advance(newAnswers, updatedResults);
    };

    const handleRangeSelect = (id) => {
        if (isViewTest) {
            const nextQuestionId = questionId + 1;
            if (nextQuestionId >= questions.length) history.goBack();
            else setQuestionId(nextQuestionId);
            return;
        }

        const updatedResults = [...results];
        updatedResults[questionId] = [id];
        setResults(updatedResults);
        advance([id], updatedResults);
    };

    const backBtnHandler = () => {
        if (questionId > 0) {
            setQuestionId(questionId - 1);
        }
    };

    const setNewAnswer = (value) => {
        if (!isViewTest) {
            setAnswers([value]);
        }
    };

    useEffect(() => {
        setIsConfirmPossible(!!answers.join("").trim());
    }, [answers]);

    const bookMainRef = useRef(null);
    const bookLeftRef = useRef(null);
    const [bookLeftMargin, setBookLeftMargin] = useState("-35px");
    const [bookLeftWide, setBookLeftWide] = useState(false);
    const [bookRightStubsRight, setBookRightStubsRight] = useState("-75px");

    useEffect(() => {
        const leftEl = bookLeftRef.current;
        const mainEl = bookMainRef.current;
        if (!leftEl || !mainEl) return;

        const ANGLE = (5 * Math.PI) / 180;
        const STUB_ANGLE = (1.5 * Math.PI) / 180;
        let leftRafId;
        let mainRafId;

        const leftObserver = new ResizeObserver(([entry]) => {
            cancelAnimationFrame(leftRafId);
            leftRafId = requestAnimationFrame(() => {
                const { width: W, height: H } = entry.contentRect;
                const M = (W / 2) * (1 - Math.cos(ANGLE)) + (H / 2) * Math.sin(ANGLE) + 5;
                setBookLeftMargin((prev) => {
                    const next = `-${M.toFixed(1)}px`;
                    return prev === next ? prev : next;
                });
            });
        });

        const mainObserver = new ResizeObserver(([entry]) => {
            cancelAnimationFrame(mainRafId);
            mainRafId = requestAnimationFrame(() => {
                const H = entry.contentRect.height;
                const wide = H > 1400;
                const R = (wide ? 105 : 92) - (H / 2) * Math.sin(STUB_ANGLE);
                setBookRightStubsRight((prev) => {
                    const next = `-${R.toFixed(1)}px`;
                    return prev === next ? prev : next;
                });
                setBookLeftWide((prev) => (prev === wide ? prev : wide));
            });
        });

        leftObserver.observe(leftEl);
        mainObserver.observe(mainEl);

        return () => {
            cancelAnimationFrame(leftRafId);
            cancelAnimationFrame(mainRafId);
            leftObserver.disconnect();
            mainObserver.disconnect();
        };
    }, [question, progressLoaded]);

    const changeAnswer = (value, index) => {
        if (!isViewTest) {
            let newAnswers = [...answers];

            if (newAnswers[index] === value) {
                newAnswers[index] = "";
            } else {
                if (question.type !== "multi") {
                    newAnswers = newAnswers.map((_, i) => (i === index ? value : ""));
                } else {
                    const countValues = answers.filter((answer) => answer !== "").length;
                    if (countValues < question.quantity) {
                        newAnswers[index] = value;
                    }
                }
            }

            setAnswers(newAnswers);
        }
    };

    const changeFreeFormAnswer = (e) => {
        if (!isViewTest) {
            setAnswers([e.target.value]);
        }
    };

    if (!question || !progressLoaded) return <Loader />;
    if (loading) return <Loader />;

    function goBack() {
        if (role === "pupil") {
            history.push(`/tests/instruction/${conditionId}`);
        } else {
            history.goBack();
        }
    }

    const paddedCurrent = questionId + 1;
    const paddedTotal = questions.length;
    const isBackActive = questionId > 0;

    return (
        <Layout hideNav>
            {!end ? (
                <div className="book-page">
                    <div className="book-spread">
                        <div
                            className="book-left"
                            ref={bookLeftRef}
                            style={{
                                marginRight: bookLeftMargin,
                                ...(bookLeftWide && { width: "190px", transform: "rotate(-2deg)" }),
                            }}
                        >
                            <button
                                className={`book-left__back${isBackActive ? " book-left__back--active" : ""}`}
                                onClick={backBtnHandler}
                                disabled={!isBackActive}
                            >
                                <svg viewBox="0 0 24 24" width="16" height="16">
                                    <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                                </svg>
                                <span>Предыдущий вопрос</span>
                            </button>

                            <div className="book-left__icons">
                                <a className="book-left__icon" href="/dashboard">
                                    <svg viewBox="0 0 24 24" width="20" height="20">
                                        <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
                                    </svg>
                                </a>
                                <a className="book-left__icon" href="/settings">
                                    <svg viewBox="0 0 24 24" width="20" height="20">
                                        <path d="M19.14 12.94c.04-.3.06-.61.06-.94s-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
                                    </svg>
                                </a>
                            </div>
                        </div>

                        <div className="book-main" ref={bookMainRef}>
                            <div className="book-main__counter">
                                {paddedCurrent}/{paddedTotal}
                            </div>

                            {question.image ? (
                                <div className="book-main__question-row">
                                    <div className="book-main__img">
                                        <img src={`${CONSTRUCTOR_LINK}/api/static/images/${question.image}`} alt="" />
                                    </div>
                                    <p
                                        className="book-main__title book-main__title--side"
                                        dangerouslySetInnerHTML={{ __html: question.name || t("choose_one_option") }}
                                    />
                                </div>
                            ) : (
                                <p
                                    className="book-main__title"
                                    dangerouslySetInnerHTML={{ __html: question.name || t("choose_one_option") }}
                                />
                            )}

                            {["Selection", "select"].includes(question.type) ? (
                                <SelectAnswers
                                    answers={answers}
                                    question={question}
                                    confirmBtnHandler={confirmBtnHandler}
                                    changeAnswer={changeAnswer}
                                    onAnswerClick={handleAnswerClick}
                                    isConfirmPossible={isConfirmPossible || isViewTest}
                                />
                            ) : ["Range", "range", "ReversedRange"].includes(question.type) ? (
                                <RangeAnswers
                                    answers={answers}
                                    question={question}
                                    setAnswer={setNewAnswer}
                                    onSelect={handleRangeSelect}
                                />
                            ) : ["Text", "text"].includes(question.type) ? (
                                <FreeFormAnswers
                                    freeFormValue={answers[0]}
                                    confirmBtnHandler={confirmBtnHandler}
                                    changeFreeFormAnswer={changeFreeFormAnswer}
                                    isConfirmPossible={isConfirmPossible || isViewTest}
                                />
                            ) : (
                                <>
                                    <p className="book-main__prompt">
                                        {t("select")} {question.quantity ? question.quantity : question.answers.length}{" "}
                                        {t("select_from")} {question.answers.length}
                                    </p>
                                    <SelectAnswers
                                        answers={answers}
                                        question={question}
                                        confirmBtnHandler={confirmBtnHandler}
                                        changeAnswer={changeAnswer}
                                        onAnswerClick={handleAnswerClick}
                                        isConfirmPossible={isConfirmPossible || isViewTest}
                                    />
                                </>
                            )}
                        </div>

                        <div
                            className="book-right-stubs"
                            style={{ right: bookRightStubsRight, ...(bookLeftWide && { width: "120px" }) }}
                        >
                            <div className="book-right-stub" />
                            <div className="book-right-stub book-right-stub--2" />
                        </div>
                    </div>
                </div>
            ) : null}
        </Layout>
    );
};
